import { useCallback, useEffect, useState } from "react";
import { fetchStalls, stallOverride } from "../lib/api";
import { AlertTriangle, Eye, MessageSquare, Repeat, SkipForward } from "lucide-react";

const MODES = [
  ["nudge", "Nudge", MessageSquare, "re-offer the same question, warmly"],
  ["rephrase", "Rephrase", Repeat, "ask the same thing far more simply"],
  ["skip", "Skip", SkipForward, "abandon it and move category"],
];

const mmss = (s) => {
  if (s === null || s === undefined) return "—";
  const m = Math.floor(s / 60);
  return m ? `${m}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
};

/** Operator-only alert rail: who has gone quiet mid-answer, and what to do about it. */
export const StallAlerts = ({ onWatch }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => fetchStalls().then(setData).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (subject, mode) => {
    setBusy(`${subject}:${mode}`);
    setNote("");
    try {
      const res = await stallOverride(subject, mode);
      setNote(`${mode} queued for ${subject.slice(0, 10)} at ${mmss(res.stall.silence_seconds)} silence`);
      await load();
    } catch (e) {
      setNote(e?.response?.data?.detail || "override failed");
    } finally {
      setBusy("");
    }
  };

  const stalled = data?.stalled || [];
  const watching = (data?.subjects || []).filter((s) => s.stall.awaiting_answer && !s.stall.stalled);

  return (
    <div data-testid="admin-stall-alerts" className="mt-6">
      {stalled.length === 0 ? (
        <div className="flex flex-wrap items-center gap-3 border border-[var(--line)] px-4 py-3">
          <span className="label">Stall watch</span>
          <span data-testid="stall-none" className="font-data text-[11px] text-zinc-500">
            nobody stalled
          </span>
          <span className="font-data text-[10px] text-zinc-700">
            {watching.length} awaiting an answer inside threshold ·{" "}
            {(data?.subjects || []).filter((s) => s.stall.online).length} online
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          {stalled.map((s) => (
            <div
              key={s.subject}
              data-testid={`stall-alert-${s.subject}`}
              className="border border-zinc-500 bg-[rgba(255,255,255,0.04)] px-4 py-4"
            >
              <div className="flex flex-wrap items-baseline gap-3">
                <AlertTriangle size={13} strokeWidth={1.8} className="text-zinc-100" />
                <span className="font-data text-[11px] uppercase tracking-[0.18em] text-zinc-100">
                  stalled · {mmss(s.stall.silence_seconds)}
                </span>
                <span className="text-xs text-zinc-200">
                  {s.name === "—" ? s.subject.slice(0, 10) : s.name}
                </span>
                <span className="font-data text-[10px] text-zinc-600">
                  threshold {s.stall.threshold_seconds}s · phase {s.phase} ·{" "}
                  {s.stall.online ? "tab open" : "tab closed"}
                  {s.stall.typing ? " · typing" : ""}
                  {s.pending_overrides ? ` · ${s.pending_overrides} override queued` : ""}
                </span>
                {onWatch && (
                  <button
                    data-testid={`stall-watch-${s.subject}`}
                    onClick={() => onWatch(s.subject)}
                    className="label ml-auto flex items-center gap-1.5 hover:text-zinc-100"
                  >
                    <Eye size={11} strokeWidth={1.7} /> watch
                  </button>
                )}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {MODES.map(([mode, label, Icon, hint]) => (
                  <button
                    key={mode}
                    data-testid={`stall-${mode}-${s.subject}`}
                    disabled={busy === `${s.subject}:${mode}`}
                    onClick={() => act(s.subject, mode)}
                    title={hint}
                    className="btn-stark flex items-center gap-2 !px-3 !py-2"
                  >
                    <Icon size={11} strokeWidth={1.8} />
                    {busy === `${s.subject}:${mode}` ? "…" : label}
                  </button>
                ))}
              </div>

              {s.last_stall_action && (
                <p className="mt-3 font-data text-[10px] text-zinc-600">
                  last action: {s.last_stall_action.mode} at{" "}
                  {s.last_stall_action.t?.slice(11, 19)} · {s.last_stall_action.by}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {note && (
        <p data-testid="stall-note" className="mt-3 font-data text-[11px] text-zinc-400">
          — {note}
        </p>
      )}
    </div>
  );
};

export default StallAlerts;
